import { Formatter } from '@softline/ui-core';
import { DataSource } from '@softapps/core';
import {
  Column,
  DeleteRow,
  EditRow,
  GetTreePath,
  DetailFactory,
  GridOptions,
  GridRowActions,
  InsertRow,
  Tree,
  TreeOptions,
  ValidateRow,
  SelectableRow,
  DetailOptions,
} from './grid';
import { MutationRepository } from '@softline/core';
import { SchemaPath } from '@angular/forms/signals';

export class GridRef<TRow extends object> {
  readonly columns: Column<TRow>[] = [];
  detail: DetailFactory<TRow, object> | null = null;
  detailOptions: DetailOptions<TRow, object> | null = null;
  options: GridOptions<TRow> | null = null;

  insertRow: InsertRow<TRow> | null = null;
  editRow: EditRow<TRow> | null = null;
  deleteRow: DeleteRow<TRow> | null = null;
  validate: ValidateRow<TRow> | null = null;
  tree: Tree<TRow> | null = null;
  selectable: SelectableRow<TRow> | null = null;
  rowActions: GridRowActions<TRow> | null = null;
  getRowClass: ((row: TRow) => string | string[] | null | undefined) | null = null;

  constructor(
    public readonly dataSource: DataSource<TRow>,
    public readonly formatter: Formatter
  ) {}

  createColumn(column: Column<TRow>): void {
    this.columns.push(column);
  }

  createDetail<TDetail extends object>(
    detail: DetailFactory<TRow, TDetail>,
    options?: DetailOptions<TRow, TDetail>
  ): void {
    this.detail = detail as DetailFactory<TRow, object>;
    this.detailOptions = options as DetailOptions<TRow, object>;
  }

  makeInsertable(
    mutations: Required<Pick<MutationRepository<TRow>, 'create'>>,
    empty: () => TRow
  ): void {
    this.insertRow = { mutations, empty };
  }

  makeEditable(mutations: Required<Pick<MutationRepository<TRow>, 'update'>>): void {
    this.editRow = { mutations };
  }

  makeDeleteable(mutations: Required<Pick<MutationRepository<TRow>, 'delete'>>): void {
    this.deleteRow = { mutations };
  }

  validateRow(validate: (schema: SchemaPath<TRow>) => void): void {
    this.validate = { validate };
  }

  selectableRow(selectable: SelectableRow<TRow>): void {
    this.selectable = selectable;
  }

  setRowActions(rowActions: GridRowActions<TRow>): void {
    this.rowActions = rowActions;
  }

  rowClass(fn: (row: TRow) => string | string[] | null | undefined): void {
    this.getRowClass = fn;
  }

  setOptions(options: GridOptions<TRow>): void {
    this.options = options;
  }

  makeTree(
    dataPath: GetTreePath<TRow>,
    options: TreeOptions<TRow> | undefined = undefined
  ): void {
    this.tree = { getTreePath: dataPath, options };
  }

  createRequest(dataSource: any): void {}
}

export class GroupColumnRef<TRow extends object> {
  readonly columns: Column<TRow>[] = [];

  constructor(public readonly formatter: Formatter) {}

  createColumn(column: Column<TRow>): void {
    this.columns.push(column);
  }

  createRequest(dataSource: any): void {}
}

export class EntityColumnRef<TEntity extends object> {
  readonly columns: Column<TEntity>[] = [];

  constructor(public readonly formatter: Formatter) {}

  createColumn(column: Column<TEntity>): void {
    this.columns.push(column);
  }

  createRequest(dataSource: any): void {}
}

export type ColumnHostRef<TRow extends object> =
  GridRef<TRow> | GroupColumnRef<TRow> | EntityColumnRef<TRow>;
